// The tab registry — shared by the shell (rail), the routes (validation),
// and the super admin's per-admin views editor. One list, no cycles.

export const TABS = [
  { key: 'overview', label: 'Overview', icon: 'grid', blurb: 'The platform at a glance' },
  { key: 'live', label: 'Live', icon: 'pulse', blurb: 'The whole system, right now' },
  // Operational tabs — shown to EVERY admin by default (null views = all of
  // these). The super-admin can deselect specific tabs per sub-admin via "Tab
  // access". Only the superOnly tabs (below) stay owner-locked, always.
  {
    key: 'finance',
    label: 'Finance',
    icon: 'card',
    blurb: 'Revenue, subscribers & run-rate',
  },
  {
    key: 'health',
    label: 'Server health',
    icon: 'server',
    blurb: 'Hardware, processes & alarms',
  },
  {
    key: 'comms',
    label: 'Comms health',
    icon: 'pulse',
    blurb: 'Email, push, webhooks & queue',
  },
  { key: 'timeline', label: 'Timeline', icon: 'clock', blurb: 'Every step, in order' },
  {
    key: 'matchmaker',
    label: 'matches',
    icon: 'match',
    blurb: 'Every pair — and the matchmaker',
  },
  { key: 'users', label: 'Users', icon: 'users', blurb: 'Souls on the platform' },
  { key: 'calls', label: 'Calls', icon: 'phone', blurb: 'The call log' },
  { key: 'reviews', label: 'Reviews', icon: 'star', blurb: 'Positive tags & connection quality' },
  { key: 'moderation', label: 'Moderation', icon: 'shield', blurb: 'Reports and blocks' },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    icon: 'card',
    blurb: 'Tiers, plans and renewals',
  },
  { key: 'quests', label: 'Chapters', icon: 'grid', blurb: 'Packs, popularity & health' },
  { key: 'ops', label: 'System', icon: 'sliders', blurb: 'Levers and environment' },
  // superOnly: visible only to the superadmin, regardless of view grants.
  // Plan editor = add/change/tweak the subscription catalog (sub-admins only
  // ever see the read-only Subscriptions tab above).
  {
    key: 'plans',
    label: 'Plan editor',
    icon: 'card',
    blurb: 'Add & edit subscription plans',
    superOnly: true,
  },
  // Chapter editor = add/change/tweak quests + their questions (season/episode).
  // Sub-admins only ever see the read-only Chapters overview tab above.
  {
    key: 'questeditor',
    label: 'Chapter editor',
    icon: 'sliders',
    blurb: 'Add & edit quests + questions',
    superOnly: true,
  },
  // Email control = edit every system email + send email/push broadcasts.
  // Super-admin only (sub-admins only see the read-only Comms health tab).
  {
    key: 'communications',
    label: 'Email control',
    icon: 'mail',
    blurb: 'Templates & broadcasts',
    superOnly: true,
  },
  // Bug reports = the in-app "Report a bug" triage queue (grouped by route,
  // push a group to ClickUp). Grantable to any approved admin (not super-only).
  {
    key: 'bugs',
    label: 'Bug reports',
    icon: 'bug',
    blurb: 'User-reported issues & ClickUp',
  },
  // ── Growth & community (owner 2026-06-15).
  {
    key: 'cac',
    label: 'Acquisition',
    icon: 'chart',
    blurb: 'Cost per member, by channel',
  },
  {
    key: 'coupons',
    label: 'Promo codes',
    icon: 'tag',
    blurb: 'Discount codes for checkout',
  },
  {
    key: 'ambassadors',
    label: 'Ambassadors',
    icon: 'megaphone',
    blurb: 'Members who promote us',
  },
  {
    key: 'badges',
    label: 'Badges',
    icon: 'badge',
    blurb: 'Assign member identity badges',
  },
  { key: 'admins', label: 'Admins', icon: 'badge', blurb: 'Roles and tab access', superOnly: true },
  {
    key: 'tribes',
    label: 'Tribes',
    icon: 'match',
    blurb: 'Private / org communities + domains',
    superOnly: true,
  },
  {
    key: 'countries',
    label: 'Countries',
    icon: 'sliders',
    blurb: 'Where olyssa is served',
    superOnly: true,
  },
] as const;

export type TabKey = (typeof TABS)[number]['key'];
export const TAB_KEYS: readonly string[] = TABS.map((t) => t.key);

// Paddle-style grouped nav (functional groups, owner 2026-06-15). Overview +
// Live stay top-level; everything else collapses into these groups. Only the
// desktop rail uses this; routing/validation/per-admin views still key off
// TABS. Every TabKey except the two TOP_NAV ones must appear in exactly one
// group (a stray key would just never show in the rail).
export const TOP_NAV: readonly TabKey[] = ['overview', 'live'];

export interface NavGroup {
  key: string;
  label: string;
  icon: string;
  tabs: readonly TabKey[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: 'souls',
    label: 'Souls & Safety',
    icon: 'match',
    tabs: ['matchmaker', 'users', 'reviews', 'moderation', 'tribes', 'countries'],
  },
  {
    key: 'callsquests',
    label: 'Calls & Chapters',
    icon: 'phone',
    tabs: ['calls', 'quests', 'questeditor'],
  },
  { key: 'money', label: 'Money', icon: 'card', tabs: ['finance', 'subscriptions', 'plans'] },
  {
    key: 'monitoring',
    label: 'Monitoring',
    icon: 'server',
    tabs: ['health', 'comms', 'communications', 'bugs'],
  },
  {
    key: 'growth',
    label: 'Growth',
    icon: 'star',
    tabs: ['cac', 'coupons', 'ambassadors', 'badges'],
  },
  { key: 'system', label: 'System', icon: 'sliders', tabs: ['timeline', 'ops', 'admins'] },
];
